import * as genericService from "./genericFormServices";
import type { LegalAdviceFormOneData } from "../types/LegalAdvices/LegalAdviceFormOne.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { CreateAttachmentDto } from "../types/MainType.type";

const ENDPOINT = "legal-advice-form-one";

export interface LegalAdviceFetchResponse {
  data: LegalAdviceFormOneData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form One
// ---------------------------------------------------------------------------
export const mapLegalAdviceBackendToFrontend = (
  item: any
): LegalAdviceFormOneData => {
  if (!item) return {} as LegalAdviceFormOneData;

  // Extract inner form values safely
  const form =
    item.legalAdviceFormOne || item.legalAdviceForm || item.formData || {};

  return {
    // Document Level Identifiers
    id: item.id || form.documentId || form.id || '',
    documentNumber: item.documentNumber || '',
    formType: item.formType || 'LEGAL_ADVICE_FORM_ONE',
    status: item.status || form.status || 'DRAFT',

    // Form Specific Text Fields
    office: form.office || '',
    township: form.township || '',
    letterNo: form.letterNo || '',
    date: form.date || null,
    refPoliceStation: form.refPoliceStation || '',
    refNo: form.refNo || '',
    complainant: form.complainant || '',
    accused: form.accused || '',
    lawSection: form.lawSection || '',
    policeOfficer: form.policeOfficer || '',
    refLetterDate: form.refLetterDate || null,
    refLetterNo: form.refLetterNo || '',
    summary: form.summary || '',
    witnessStatement: form.witnessStatement || '',
    accusedStatement: form.accusedStatement || '',
    seizedEvidence: form.seizedEvidence || '',
    medicalEvidence: form.medicalEvidence || '',
    analysis: form.analysis || '',
    requirements: form.requirements || '',
    legalAdvice: form.legalAdvice || '',

    // Signature & Signer Details
    lawOffice: form.lawOffice || '',
    policeoffice: form.policeoffice || '',
    copyTo: form.copyTo || '',
    signName: form.signName || form.signerName || '',
    signerName: form.signerName || form.signName || '',
    signatureImage: formatSignatureUrl(
      form.signatureImage || item.signatureImage
    ),

    // Geo Levels (FIXED: Added targetGeoLevels and originGeoLevel)
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata (User, Office, Logs)
    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
  ? item.stageHistory.map((history: any) => ({
      id: history.id,
      action: history.action,
      comments: history.comments,
      attachments: history.attachments,
      createdAt: history.createdAt,
      performedBy: history.performedBy
        ? {
            id: history.performedBy.id,
            name: history.performedBy.name,
            role: history.performedBy.role,
            position: history.performedBy.position,
            office: history.performedBy.office
              ? {
                  id: history.performedBy.office.id,
                  name: history.performedBy.office.name,
                  code: history.performedBy.office.code,
                  level: history.performedBy.office.level,
                }
              : undefined,
          }
        : undefined,
    }))
  : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form One
// ---------------------------------------------------------------------------
const legalAdviceFilterPredicate = (
  app: LegalAdviceFormOneData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const matchesDate =
    !selectedDate || Boolean(app.date && app.date.startsWith(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.refNo && app.refNo.toLowerCase().includes(term)) ||
    Boolean(app.complainant && app.complainant.toLowerCase().includes(term)) ||
    Boolean(app.accused && app.accused.toLowerCase().includes(term)) ||
    Boolean(app.lawSection && app.lawSection.toLowerCase().includes(term)) ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions (Office User - Form Endpoint)
// ---------------------------------------------------------------------------

// Fetch All / Filtered Applications
export const fetchLegalAdviceApplications = (
  params: FetchParams
): Promise<LegalAdviceFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormOneData>(
    ENDPOINT,
    params,
    mapLegalAdviceBackendToFrontend,
    legalAdviceFilterPredicate
  );

// Fetch Single Application by ID
export const fetchLegalAdviceById = (
  id: string
): Promise<LegalAdviceFormOneData> =>
  genericService.fetchApplicationById<LegalAdviceFormOneData>(
    ENDPOINT,
    id,
    mapLegalAdviceBackendToFrontend
  );

// Save Draft (Create POST or Update PUT)
export const saveLegalAdviceDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormOneData>
) => genericService.saveDraft<LegalAdviceFormOneData>(ENDPOINT, id, data);

// Submit Form (Includes Signatures inside DTO)
export const submitLegalAdviceForm = (
  id: string,
  data: LegalAdviceFormOneData
) => genericService.submitForm<LegalAdviceFormOneData>(ENDPOINT, id, data);

// Delete Form (Soft Delete)
export const deleteLegalAdviceForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions (Global DocumentController)
// ---------------------------------------------------------------------------

export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);


// Approve Form (PATCH /documents/:id/approve)
export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (
  file: File
) => genericService.uploadFile(file);
// // Return Form for Revisions (PATCH /documents/:id/return)
// export const returnLegalAdviceForm = (id: string, remark?: string) =>
//   genericService.returnForm(id, remark);

// // Reject Form (PATCH /documents/:id/reject)
// export const rejectLegalAdviceForm = (id: string, remark?: string) =>
//   genericService.rejectForm(id, remark);